const NAMESPACE = "editTheme";

const KEYS = {
    nameInputLabel: "nameInputLabel",
    optionsSectionHeader: "optionsSectionHeader",
    enableRoundedCornersLabel: "enableRoundedCornersLabel",
    fontFamilyLabel: "fontFamilyLabel",
    fontSizeLabel: "fontSizeLabel",
    sitewidePreviewButtonLabel: "sitewidePreviewButtonLabel",
    updatePreviewButtonLabel: "updatePreviewButtonLabel",
    endPreviewButtonLabel: "endPreviewButtonLabel",
    quickPreviewSectionHeader: "quickPreviewSectionHeader",
    wcagAccessibilityLinkText: "wcagAccessibilityLinkText",
    primaryColorLabel: "primaryColorLabel",
    secondaryColorLabel: "secondaryColorLabel",
    grayColorLabel: "grayColorLabel",
    successColorLabel: "successColorLabel",
    infoColorLabel: "infoColorLabel",
    warningColorLabel: "warningColorLabel",
    dangerColorLabel: "dangerColorLabel",
    primaryButtonContrastRatioLabel: "primaryButtonContrastRatioLabel",
    secondaryButtonContrastRatioLabel: "secondaryButtonContrastRatioLabel",
    compilingThemeLoadingMessage: "compilingThemeLoadingMessage",
    sitewidePreviewCompleteMessage: "sitewidePreviewCompleteMessage",
    sitewidePreviewErrorDialogTitle: "sitewidePreviewErrorDialogTitle",
    sitewidePreviewErrorDialogMessage: "sitewidePreviewErrorDialogMessage",
    sitewidePreviewResetDialogMessage: "sitewidePreviewResetDialogMessage",
    unsavedChangesDialogTitle: "unsavedChangesDialogTitle",
    unsavedChangesDialogMessage: "unsavedChangesDialogMessage",
    alertContrastRatioTitle: "alertContrastRatioTitle",
    alertContrastRatioMessage: "alertContrastRatioMessage",
    exampleTextInputLabel: "exampleTextInputLabel",
    exampleTextInputPlaceholder: "exampleTextInputPlaceholder",
    exampleCheckboxLabel: "exampleCheckboxLabel",
    exampleSwitchLabel: "exampleSwitchLabel",
    exampleRadioGroupLabel: "exampleRadioGroupLabel",
    examplePrimaryBtnLabel: "examplePrimaryBtnLabel",
    exampleSecondaryBtnLabel: "exampleSecondaryBtnLabel",
    primaryColorDescription: "primaryColorHelp",
    secondaryColorDescription: "secondaryColorHelp",
    grayColorDescription: "grayColorHelp",
    mainColorsSectionHeader: "mainColorsSectionHeader",
    alertColorsSectionHeader: "alertColorsSectionHeader",
    typographyOptionsSectionHeader: "typographyOptionsSectionHeader",
    appearanceOptionsSectionHeader: "appearanceOptionsSectionHeader",
    quickPreviewSectionDescription: "quickPreviewSectionDescription",
    exampleItemsHeader: "exampleItemsHeader",
    exampleItemText: "exampleItemText",
    exampleThingsHeader: "exampleThingsHeader",
    exampleThingText: "exampleThingText",
    exampleCategoriesHeader: "exampleCategoriesHeader",
    exampleCategoryText: "exampleCategoryText",
    confirmEndPreviewDialogTitle: "confirmEndPreviewDialogTitle",
    confirmEndPreviewDialogMessage: "confirmEndPreviewDialogMessage",
    sitewidePreviewNoChangesMessage: "sitewidePreviewNoChangesMessage"
};

const MESSAGES = {
    [KEYS.nameInputLabel]: {
        value: "Name:",
        comment: "Label for the input where the theme name can be entered, with a colon."
    },
    [KEYS.optionsSectionHeader]: {
        value: "Options",
        comment: "Header text for the section about options to apply to this site."
    },
    [KEYS.enableRoundedCornersLabel]: {
        value: "Rounded corners",
        comment: "Label for a checkbox that will enable rounded corners on page elements."
    },
    [KEYS.fontFamilyLabel]: {
        value: "Font family:",
        comment: "Label for the font family drop-down selector, with a colon."
    },
    [KEYS.fontSizeLabel]: {
        value: "Font size: {fontSize}px",
        comment: "Label for the size of the font in pixels, e.g. 'Font size: 16px'."
    },
    [KEYS.sitewidePreviewButtonLabel]: {
        value: "Preview Theme Sitewide",
        comment: "Label for the button that will show a preview of the site with the current theme applied."
    },
    [KEYS.updatePreviewButtonLabel]: {
        value: "Update Sitewide Preview",
        comment: "Label for the button that will update the preview of the site with the current theme colors."
    },
    [KEYS.endPreviewButtonLabel]: {
        value: "End Preview",
        comment:
            "Label for the button that will reset the preview of the site to the default theme and end the local preview."
    },
    [KEYS.quickPreviewSectionHeader]: {
        value: "Quick Preview",
        comment:
            "Header text for the quick preview section where the theme inputs can be seen on a small sample of elements."
    },
    [KEYS.sampleText]: {
        value: "Sample Text",
        comment: "Meaningless filler text that gets shown to compare text and background colors."
    },
    [KEYS.wcagAccessibilityLinkText]: {
        value: "WCAG 2.0 AA",
        comment: "Text for a link that will take the user to a resource about WCAG 2.0 AA compliance."
    },

    // This is called "Brand" in the UI, and "Primary" internally because Bootstrap refers to it as
    // "Primary". Similar reason applies for "Neutral" (Secondary) and "Secondary Neutral" (Gray) below.
    [KEYS.primaryColorLabel]: {
        value: "Brand",
        comment: "Label for an input where the primary (brand) color can be selected."
    },
    [KEYS.secondaryColorLabel]: {
        value: "Neutral",
        comment: "Label for an input where the secondary (neutral) color can be selected."
    },
    [KEYS.grayColorLabel]: {
        value: "Secondary Neutral",
        comment: "Label for an input where the gray (secondary neutral) color can be selected."
    },
    [KEYS.successColorLabel]: {
        value: "Success",
        comment: "Label for an input where the success color can be selected."
    },
    [KEYS.infoColorLabel]: { value: "Info", comment: "Label for an input where the info color can be selected." },
    [KEYS.warningColorLabel]: {
        value: "Warning",
        comment: "Label for an input where the warning color can be selected."
    },
    [KEYS.dangerColorLabel]: { value: "Error", comment: "Label for an input where the danger color can be selected." },
    [KEYS.primaryButtonContrastRatioLabel]: {
        value: "Primary Button Contrast Ratio: {contrastRatio}",
        comment: "Label for a button that shows the contrast ratio of the colors on a 'primary' button."
    },
    [KEYS.secondaryButtonContrastRatioLabel]: {
        value: "Secondary Button Contrast Ratio: {contrastRatio}",
        comment: "Label for a button that shows the contrast ratio of the colors on a 'secondary' button."
    },
    [KEYS.compilingThemeLoadingMessage]: {
        value: "Compiling theme, please do not navigate away from this page.",
        comment: "Message that is shown while the theme is being compiled."
    },
    [KEYS.sitewidePreviewCompleteMessage]: {
        value: "Theme compilation complete. Page will now refresh to apply the changes.",
        comment: "Message that is shown after the sitewide preview has been generated."
    },
    [KEYS.sitewidePreviewErrorDialogTitle]: {
        value: "Sitewide Preview Error",
        comment: "Title for the dialog that appears when there is an error generating the sitewide preview."
    },
    [KEYS.sitewidePreviewErrorDialogMessage]: {
        value: "An error occurred while compiling the theme. Please try again.",
        comment: "Message for the dialog that appears when there is an error generating the sitewide preview."
    },
    [KEYS.sitewidePreviewResetDialogMessage]: {
        value: "Theme preview has ended. The page will now refresh to apply the changes.",
        comment: "Message for the dialog that appears when the user wants to reset the sitewide preview."
    },
    [KEYS.unsavedChangesDialogTitle]: {
        value: "Unsaved Changes",
        comment: "Title for the dialog that appears when the user tries to navigate away with unsaved changes."
    },
    [KEYS.unsavedChangesDialogMessage]: {
        value: "The page will refresh to apply the preview. Do you want to save the changes to this theme?",
        comment: "Message for the dialog that appears when the user tries to navigate away with unsaved changes."
    },
    [KEYS.alertContrastRatioTitle]: {
        value: "{alertType} Alert Title - contrast ratio: {contrastRatio}",
        comment:
            "Text on an example alert banner displaying the kind of alert (e.g. Warning Alert) and the contrast ratio."
    },
    [KEYS.alertContrastRatioMessage]: {
        value: "Preview of {alertType} alert content",
        comment: "Example text on a preview alert banner of a certain type (e.g. Warning Alert)."
    },
    [KEYS.exampleTextInputLabel]: {
        value: "Text input:",
        comment: "Label for an example text input field, with a colon."
    },
    [KEYS.exampleTextInputPlaceholder]: {
        value: "Placeholder text",
        comment: "Placeholder text for an example text input field."
    },
    [KEYS.exampleCheckboxLabel]: {
        value: "Checkbox",
        comment: "Label for an example checkbox."
    },
    [KEYS.exampleSwitchLabel]: {
        value: "Checkbox switch",
        comment: "Label for an example checkbox switch."
    },
    [KEYS.exampleRadioGroupLabel]: {
        value: "Radio button group",
        comment: "Label for an example radio group."
    },
    [KEYS.examplePrimaryBtnLabel]: {
        value: "Primary Action",
        comment: "Label on a primary-color button that is an example of invoking some arbitrary action."
    },
    [KEYS.exampleSecondaryBtnLabel]: {
        value: "Secondary Action",
        comment: "Label on a secondary-color button that is an example of invoking some arbitrary action."
    },
    [KEYS.primaryColorDescription]: {
        value: "The primary brand color defines the look and feel of the site and stands out to users. Examples include the navigation bar, primary buttons, and table hover background colors.",
        comment: "Help text for the brand (primary) color input."
    },
    [KEYS.secondaryColorDescription]: {
        value: "The neutral color is an accent that complements the primary brand color, helps it stand out, and provides emphasis and depth to users. Examples of this include secondary buttons, loading message box, and text input button background colors.",
        comment: "Help text for the neutral (secondary) color input."
    },
    [KEYS.grayColorDescription]: {
        value: "The secondary neutral color helps provide balance and versatility to the design, supporting both the brand and neutral colors. Examples include input element border, input element read-only background, and table accent colors.",
        comment: "Help text for the secondary neutral (gray) color input."
    },
    [KEYS.mainColorsSectionHeader]: {
        value: "Main colors",
        comment: "Header text for the section where the main colors can be selected."
    },
    [KEYS.alertColorsSectionHeader]: {
        value: "Alert colors",
        comment: "Header text for the section where the alert colors can be selected."
    },
    [KEYS.typographyOptionsSectionHeader]: {
        value: "Typography",
        comment: "Header text for the section where the typography options can be selected."
    },
    [KEYS.appearanceOptionsSectionHeader]: {
        value: "Appearance",
        comment: "Header text for the section where the appearance options can be selected."
    },
    [KEYS.quickPreviewSectionDescription]: {
        value: "This section has sample components for previewing the selected theme choices. When a theme is submitted for review, PowerClerk reviewers will test theme settings accessibility against WCAG 2.0 AA criteria. For more information, see the guidelines at the link below.",
        comment:
            "Description for the quick preview section that briefly describes accessibility compliance and a link to the full source for those standards."
    },
    [KEYS.exampleItemsHeader]: {
        value: "Items",
        comment: "Header text for the section with example items."
    },
    [KEYS.exampleItemText]: {
        value: "Item {n}",
        comment: "Example text for an arbitrary item followed by a number, e.g. Item 1."
    },
    [KEYS.exampleThingsHeader]: {
        value: "Things",
        comment: "Header text for the section with example things."
    },
    [KEYS.exampleThingText]: {
        value: "Thing {n}",
        comment: "Example text for an arbitrary thing optionally followed by a number, e.g. Thing 2."
    },
    [KEYS.exampleCategoriesHeader]: {
        value: "Categories",
        comment: "Header text for the section with example categories."
    },
    [KEYS.exampleCategoryText]: {
        value: "Category {n}",
        comment: "Example text for an arbitrary category optionally followed by a number, e.g. Category 3."
    },
    [KEYS.confirmEndPreviewDialogTitle]: {
        value: "Confirm End Preview",
        comment: "Title for the dialog that appears when the user wants to end the preview."
    },
    [KEYS.confirmEndPreviewDialogMessage]: {
        value: "Are you sure you want to end the preview?",
        comment: "Message for the dialog that appears when the user wants to end the preview."
    },
    [KEYS.sitewidePreviewNoChangesMessage]: {
        value: "Preview is the same as the theme that is currently applied or previewed.",
        comment: "Message that is shown when the user tries to preview the theme without making any changes."
    }
};

export { KEYS, NAMESPACE, MESSAGES };
